import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { LEAD } from "../config/content";
import { submitLead } from "../lib/api";
import { FadeIn } from "./Reveal";

export const EmailCapture = ({ sessionId }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | done | dismissed
  const [sentOk, setSentOk] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      const res = await submitLead({
        session_id: sessionId,
        name: name.trim() || null,
        email: email.trim(),
        marketing_consent: consent,
        consent_version: LEAD.consentVersion,
      });
      setSentOk(Boolean(res.data?.email_sent));
      setStatus("done");
    } catch {
      setStatus("idle");
      toast.error("Qualcosa non ha funzionato. Riprova tra un momento.");
    }
  };

  if (status === "dismissed") {
    return (
      <FadeIn className="mt-4 pb-4 text-center">
        <p data-testid="lead-dismissed" className="font-serif italic text-lg text-muted2">
          {LEAD.dismissedLine}
        </p>
      </FadeIn>
    );
  }

  if (status === "done") {
    return (
      <FadeIn className="mt-4 pb-4 text-center">
        <div className="border border-gold/60 px-6 py-10">
          <p data-testid="lead-success-title" className="font-serif text-2xl text-ink">
            {LEAD.successTitle}
          </p>
          <p data-testid="lead-success-body" className="mt-3 text-sm text-muted2">
            {sentOk ? LEAD.successBody : LEAD.successBodyNoSend}
          </p>
        </div>
      </FadeIn>
    );
  }

  return (
    <FadeIn className="mt-4 pb-4">
      <form
        onSubmit={submit}
        data-testid="lead-form"
        className="border border-line bg-surface/40 px-6 py-8"
      >
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">
          {LEAD.title}
        </p>
        <p className="mt-3 font-serif text-lg leading-relaxed text-ink">{LEAD.body}</p>

        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={LEAD.namePlaceholder}
          data-testid="lead-name-input"
          className="mt-6 w-full bg-transparent border border-line px-4 py-3.5 text-ink placeholder:text-muted2/60 focus:outline-none focus:border-gold transition-colors"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={LEAD.emailPlaceholder}
          data-testid="lead-email-input"
          className="mt-3 w-full bg-transparent border border-line px-4 py-3.5 text-ink placeholder:text-muted2/60 focus:outline-none focus:border-gold transition-colors"
        />

        <label className="mt-5 flex items-start gap-3 cursor-pointer select-none">
          <span
            data-testid="marketing-consent-checkbox"
            onClick={(e) => {
              e.preventDefault();
              setConsent(!consent);
            }}
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors duration-300 ${
              consent ? "bg-olive border-olive" : "border-line bg-transparent"
            }`}
          >
            {consent && <Check className="w-3.5 h-3.5 text-ivory" />}
          </span>
          <span className="text-xs leading-relaxed text-muted2">
            {LEAD.consentLabel}{" "}
            <Link to="/privacy" className="underline underline-offset-4 text-ink">
              Privacy Policy
            </Link>
          </span>
        </label>

        <motion.button
          type="submit"
          disabled={status === "sending"}
          data-testid="lead-submit-button"
          whileTap={{ scale: 0.98 }}
          className="mt-6 w-full bg-ink px-6 py-4 text-ivory font-mono text-xs tracking-[0.3em] transition-colors duration-500 hover:bg-olive disabled:opacity-50"
        >
          {status === "sending" ? "INVIO…" : LEAD.submitLabel}
        </motion.button>

        <button
          type="button"
          onClick={() => setStatus("dismissed")}
          data-testid="lead-skip-button"
          className="mt-4 w-full text-center font-mono text-[10px] tracking-[0.25em] uppercase text-muted2 transition-colors hover:text-ink"
        >
          {LEAD.skipLabel}
        </button>
      </form>
    </FadeIn>
  );
};
